import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { IntegrationUserConnectionOrigin } from "@custom-types/integrations-types";
import { useToast } from "@flat-ui";
import { FaroCoreApiConst } from "@stellar/core-api-client";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useState } from "react";
/** @returns properties and methods commonly used for integration workflows at user level */
export function useUserIntegrations({
  integrationId,
  tokensService
}) {
  const [dialogState, setDialogState] = useState("none");
  const [tokens, setTokens] = useState([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();

  /** Filtering the available integrations and enabled integrations */
  const allIntegrations = useMemo(() => {
    const integrationIds = FaroCoreApiConst.allIntegrationIds;
    const enabledIntegrationSet = new Set(integrationIds.filter(id => tokens.some(token => token.provider === id)));
    return {
      enabledIntegrationIds: enabledIntegrationSet,
      availableIntegrationIds: integrationIds
    };
  }, [tokens]);

  /** Fetches all integration tokens from backend through the integration service */
  useEffect(() => {
    async function getIntegrationTokens() {
      try {
        const integrationTokens = await tokensService.getIntegrationTokens();
        setTokens(integrationTokens);
      } catch (error) {
        handleErrorWithToast({
          id: `getIntegrationTokens-${Date.now().toString()}`,
          title: "Failed to get the integration tokens.",
          error
        });
      } finally {
        setIsLoadingIntegrations(false);
      }
    }
    void getIntegrationTokens();
  }, [handleErrorWithToast, tokensService]);

  /** Enable an integration */
  const onEnableIntegration = useCallback(async integrationId_0 => {
    trackEvent({
      name: UserProfile.connectIntegration,
      props: {
        integrationId: integrationId_0,
        from: IntegrationUserConnectionOrigin.integrationAccounts
      }
    });
    setDialogState("inProgress");
    try {
      const integrationTokens_0 = await tokensService.authorizeIntegration(integrationId_0);
      setTokens(integrationTokens_0);
      setDialogState("success");
    } catch (error_0) {
      handleErrorWithToast({
        id: `authorizeIntegration-${Date.now().toString()}`,
        title: "Failed to authorize the integration.",
        error: error_0
      });
      setDialogState("none");
    }
  }, [handleErrorWithToast, tokensService, trackEvent]);

  /** Disable an integration */
  const onDisableIntegration = useCallback(async integrationId_1 => {
    trackEvent({
      name: UserProfile.disconnectIntegration,
      props: {
        integrationId: integrationId_1,
        from: IntegrationUserConnectionOrigin.integrationAccounts
      }
    });
    setDialogState("disconnect");
    try {
      const integrationTokens_1 = await tokensService.revokeAuthorization(integrationId_1);
      setTokens(integrationTokens_1);
      openToast({
        title: "Integration disconnected"
      });
    } catch (error_1) {
      handleErrorWithToast({
        id: `disconnectIntegration-${Date.now().toString()}`,
        title: "Failed to disconnect the integration.",
        error: error_1
      });
    }
    setDialogState("none");
  }, [handleErrorWithToast, tokensService, openToast, trackEvent]);

  /** Close the other tab if user cancel the integration enabling process from dashboard tab */
  const onCloseAuthorizationWindow = useCallback(() => {
    tokensService.closeAuthorizationWindow();
    setDialogState("none");
  }, [tokensService]);

  /** Callback when confirm button clicked on success dialog */
  function onSuccessDialogConfirm() {
    setDialogState("none");
  }

  /** Callback when cancel button clicked on in progress dialog */
  function onInProgressDialogCancel() {
    onCloseAuthorizationWindow();
  }

  /** Callback when confirm button clicked on disconnect dialog */
  function onDisconnectDialogConfirm() {
    void onDisableIntegration(integrationId);
  }

  /** Callback when cancel button clicked on disconnect dialog */
  function onDisconnectDialogCancel() {
    setDialogState("none");
  }
  return {
    allIntegrations,
    isLoadingIntegrations,
    onEnableIntegration,
    dialogState,
    setDialogState,
    onSuccessDialogConfirm,
    onInProgressDialogCancel,
    onDisconnectDialogConfirm,
    onDisconnectDialogCancel
  };
}